import { useEffect, useMemo, useState, useRef } from "react";
import { hasRole, isFinanceLead } from "@/lib/roles";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { employeeMatches } from "@/lib/employeeSearch";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import { askDeductionStart } from "@/lib/loanDeduction";
import { Plus, X, Check, Ban, Trash2, Wallet, HandCoins, Search, Send, FileDown, FileText, Download, Upload, Inbox } from "lucide-react";
import { MoneyInput } from "@/components/MoneyInput";

function fmt(n) {
    if (n == null) return "₦0";
    return "₦" + Number(n).toLocaleString("en-NG", { maximumFractionDigits: 0 });
}

const STATUS_PILLS = {
    pending: { label: "Pending", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    accountant_verified: { label: "Acct Verified", cls: "bg-sky-50 text-sky-700 border-sky-200" },
    dept_approved: { label: "Dept Approved", cls: "bg-violet-50 text-violet-700 border-violet-200" },
    finance_approved: { label: "Finance Approved", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    approved: { label: "Approved", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    disbursed: { label: "Disbursed", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    completed: { label: "Completed", cls: "bg-green-50 text-green-700 border-green-200" },
    rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
    cancelled: { label: "Cancelled", cls: "bg-ink/5 text-ink/70 border-ink/10" },
};

function StatusPill({ status }) {
    const s = STATUS_PILLS[status] || { label: status, cls: "bg-ink/5 text-ink/70 border-ink/10" };
    return (
        <span className={`inline-flex items-center px-2 py-1 border text-[11px] uppercase tracking-[0.15em] ${s.cls}`}>
            {s.label}
        </span>
    );
}

export default function Loans() {
    const { user } = useAuth();
    const { confirm, prompt } = useConfirm();
    const role = user?.role || "";
    const isFinLead = isFinanceLead(user);
    // Approve / reject — Admin, HR, or Finance Lead
    const canApprove = hasRole(user, "admin", "hr") || isFinLead;
    // Disburse — Admin or Accountant
    const canDisburse = hasRole(user, "admin", "accountant");
    // Manage (delete / create) — Admin or HR
    const canManage = hasRole(user, "admin", "hr");
    const [activeTab, setActiveTab] = useState("loan"); // "loan" | "advance"
    const [loans, setLoans] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ employee_id: "", type: "loan", amount: 0, tenure_months: 6, reason: "", start_period: "" });
    const [bulkOpen, setBulkOpen] = useState(false);
    const [openingOpen, setOpeningOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: loansData }, { data: empData }] = await Promise.all([
                api.get("/loans", { params: { status: statusFilter !== "all" ? statusFilter : undefined } }),
                api.get("/employees"),
            ]);
            setLoans(loansData);
            setEmployees(empData);
        } catch (e) {
            toast.error("Failed to load loans");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

    const onCreate = async (e) => {
        e.preventDefault();
        if (!form.employee_id) { toast.error("Pick an employee"); return; }
        if (form.amount === "" || Number.isNaN(Number(form.amount)) || Number(form.amount) < 0) { toast.error("Enter an amount of ₦0 or more"); return; }
        setCreating(true);
        try {
            await api.post("/loans", { ...form, amount: Number(form.amount), tenure_months: Number(form.tenure_months) });
            toast.success("Request submitted");
            setOpen(false);
            setForm({ employee_id: "", type: "loan", amount: 0, tenure_months: 6, reason: "", start_period: "" });
            setEmpSearch("");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Create failed");
        } finally {
            setCreating(false);
        }
    };

    const setStatus = async (loan, status) => {
        let startPeriod;
        if (status === "disbursed") {
            startPeriod = await askDeductionStart(prompt, loan, fmt);
            if (!startPeriod) return;
        }
        const opts = {
            approved: {
                title: "Final Finance approval",
                description: `${loan.employee_name} · ${loan.type} · ${fmt(loan.amount)}. This builds the installment schedule and notifies the employee.`,
                confirmLabel: "Approve",
            },
            rejected: { title: "Reject this request?", description: "The employee will be notified by email.", confirmLabel: "Reject", danger: true },
            cancelled: { title: "Cancel this request?", confirmLabel: "Cancel request", danger: true },
        };
        if (opts[status]) {
            const ok = await confirm(opts[status]);
            if (!ok) return;
        }
        const actionMap = { approved: "finance_approve", rejected: "reject", disbursed: "disburse", cancelled: "cancel" };
        try {
            await api.put(`/loans/${loan.id}`, { action: actionMap[status] || status, start_period: startPeriod });
            toast.success(status === "disbursed" ? `Marked disbursed — deductions start ${startPeriod}` : `Marked ${status}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
        }
    };

    const onDelete = async (loan) => {
        const ok = await confirm({
            title: `Delete ${loan.type} request?`,
            description: `For ${loan.employee_name} · ${fmt(loan.amount)}. This cannot be undone.`,
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/loans/${loan.id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
        }
    };

    const downloadBankAdvice = async (loan) => {
        try {
            const res = await api.get(`/loans/${loan.id}/bank-advice`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            const safeName = (loan.employee_name || "employee").replace(/\s+/g, "_");
            a.download = `bank_advice_${loan.type || "loan"}_${safeName}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Bank advice downloaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Download failed");
        }
    };

    const [empSearch, setEmpSearch] = useState("");
    const filteredEmployees = useMemo(() => {
        const q = empSearch.trim().toLowerCase();
        if (!q) return employees.slice(0, 50);
        return employees.filter((e) => employeeMatches(e, q)).slice(0, 50);
    }, [empSearch, employees]);
    const selectedEmp = useMemo(
        () => employees.find((e) => e.id === form.employee_id),
        [form.employee_id, employees],
    );

    const filteredLoans = useMemo(
        () => loans.filter((l) => (l.type || "loan") === activeTab),
        [loans, activeTab],
    );

    const monthlyPreview = useMemo(() => {
        const a = Number(form.amount) || 0;
        const t = Math.max(Number(form.tenure_months) || 1, 1);
        return a / t;
    }, [form.amount, form.tenure_months]);

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="loans-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Cash advances</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Loans &amp; salary advances
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-xl">
                        {activeTab === "loan"
                            ? "Multi-month staff loans. Approved installments are deducted automatically from each subsequent payroll run."
                            : "Single-shot salary advances. The full amount is deducted at the very next payroll run after approval."}
                    </p>
                </div>
                <button
                    onClick={() => setBulkOpen(true)}
                    className="inline-flex items-center gap-2 border border-ink/20 px-5 py-3 text-sm hover:bg-ink/5"
                    data-testid="bulk-loans-button"
                    style={canManage ? {} : { display: "none" }}
                >
                    <FileText className="w-4 h-4" /> Bulk via Excel
                </button>
                <button
                    onClick={() => setOpeningOpen(true)}
                    className="inline-flex items-center gap-2 border border-ink/20 px-5 py-3 text-sm hover:bg-ink/5"
                    data-testid="opening-balances-button"
                    style={canManage ? {} : { display: "none" }}
                    title="Import carry-over loan / advance balances from your previous system"
                >
                    <FileText className="w-4 h-4" /> Opening balances
                </button>
                <button
                    onClick={() => { setForm({ employee_id: "", type: activeTab, amount: 0, tenure_months: activeTab === "loan" ? 6 : 1, reason: "", start_period: "" }); setOpen(true); }}
                    className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90"
                    data-testid="add-loan-button"
                >
                    <Plus className="w-4 h-4" /> {activeTab === "loan" ? "New loan" : "New advance"}
                </button>
            </div>

            <div className="mt-10 flex items-center gap-1 border-b border-ink/10">
                {[{ key: "loan", label: "Loans", icon: Wallet }, { key: "advance", label: "Salary advances", icon: HandCoins }].map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setActiveTab(t.key)}
                            className={`flex items-center gap-2 px-5 py-3 text-sm border-b-2 -mb-px ${
                                activeTab === t.key ? "border-klein text-ink font-medium" : "border-transparent text-ink/50 hover:text-ink"
                            }`}
                            data-testid={`loans-type-tab-${t.key}`}
                        >
                            <Icon className="w-4 h-4" /> {t.label}
                            <span className="ml-1 text-xs text-ink/40">{loans.filter((l) => (l.type || "loan") === t.key).length}</span>
                        </button>
                    );
                })}
            </div>

            <div className="mt-6 flex items-center gap-3 px-4 py-3 bg-indigo-50/60 border border-indigo-200 text-indigo-900 text-sm" data-testid="loans-workflow-hint">
                <Inbox className="w-4 h-4 shrink-0" />
                <span className="flex-1">
                    {role === "employee"
                        ? "Your request goes through Accountant verify → Dept Lead approve → Finance Lead approve → Accountant disburse. You'll be emailed at each stage."
                        : <>Approval workflow: <strong>Accountant verify</strong> → <strong>Dept Lead approve</strong> → <strong>Finance Lead approve</strong> → <strong>Accountant disburse</strong>. Use the Pending Approvals inbox to action requests at your stage.</>}
                </span>
                {role !== "employee" && (
                <Link to="/pending-approvals" className="underline font-medium" data-testid="loans-open-pending-approvals">
                    Open inbox →
                </Link>
                )}
            </div>

            <div className="mt-6 flex items-center gap-2 flex-wrap">
                {["all", "pending", "accountant_verified", "dept_approved", "finance_approved", "disbursed", "completed", "rejected"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] border ${
                            statusFilter === s ? "bg-ink text-white border-ink" : "border-ink/15 text-ink/70 hover:border-ink/40"
                        }`}
                        data-testid={`loans-filter-${s}`}
                    >
                        {s.replace(/_/g, " ")}
                    </button>
                ))}
            </div>

            <div className="mt-6 bg-white border border-ink/10 overflow-auto max-h-[420px]">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="border-b-2 border-ink/20">
                            <th className="text-left overline px-4 py-3">Employee</th>
                            <th className="text-right overline px-4 py-3">Amount</th>
                            <th className="text-right overline px-4 py-3 hidden lg:table-cell">{activeTab === "loan" ? "Monthly" : "Approved on"}</th>
                            <th className="text-right overline px-4 py-3 hidden lg:table-cell">{activeTab === "loan" ? "Tenure" : "Period"}</th>
                            <th className="text-right overline px-4 py-3 hidden md:table-cell">Balance</th>
                            <th className="text-left overline px-4 py-3">Status</th>
                            <th className="text-right overline px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="8" className="px-6 py-16 text-center text-sm text-ink/40">Loading…</td></tr>
                        ) : filteredLoans.length === 0 ? (
                            <tr><td colSpan="8" className="px-6 py-16 text-center text-sm text-ink/40">No {activeTab === "loan" ? "loans" : "advances"} yet.</td></tr>
                        ) : (
                            filteredLoans.map((loan) => (
                                <tr key={loan.id} className="border-b border-ink/10 hover:bg-bone/40" data-testid={`loan-row-${loan.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="text-ink font-medium">{loan.employee_name}</div>
                                        <div className="text-xs text-ink/50 truncate">
                                            {loan.employee_no || "—"} {loan.department ? `· ${loan.department}` : ""}
                                        </div>
                                        {loan.reason && <div className="text-xs text-ink/40 mt-0.5 truncate max-w-[260px]">{loan.reason}</div>}
                                    </td>
                                    <td className="px-4 py-3 text-right tabular-nums">{fmt(loan.amount)}</td>
                                    <td className="px-4 py-3 text-right tabular-nums hidden lg:table-cell">
                                        {activeTab === "loan"
                                            ? fmt(loan.monthly_installment)
                                            : (loan.approved_at ? new Date(loan.approved_at).toLocaleDateString() : "—")}
                                    </td>
                                    <td className="px-4 py-3 text-right hidden lg:table-cell" data-testid={`loan-start-${loan.id}`}>
                                        {activeTab === "loan" ? `${loan.tenure_months}m` : "Single"}
                                        {loan.start_period && <div className="text-[11px] text-ink/50">from {loan.start_period}</div>}
                                    </td>
                                    <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell">{fmt(loan.balance)}</td>
                                    <td className="px-4 py-3"><StatusPill status={loan.status} /></td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-1">
                                            {canApprove && loan.status === "pending" && (
                                                <>
                                                    <button onClick={() => setStatus(loan, "approved")} className="p-2 text-green-700 hover:bg-green-50" title="Approve" data-testid={`loan-approve-${loan.id}`}>
                                                        <Check className="w-4 h-4" />
                                                    </button>
                                                    <button onClick={() => setStatus(loan, "rejected")} className="p-2 text-red-700 hover:bg-red-50" title="Reject" data-testid={`loan-reject-${loan.id}`}>
                                                        <Ban className="w-4 h-4" />
                                                    </button>
                                                </>
                                            )}
                                            {canDisburse && (loan.status === "approved" || loan.status === "finance_approved") && (
                                                <button
                                                    onClick={() => setStatus(loan, "disbursed")}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs uppercase tracking-[0.12em] border border-klein text-klein hover:bg-klein hover:text-white"
                                                    title="Mark as disbursed (Accountant)"
                                                    data-testid={`loan-disburse-${loan.id}`}
                                                >
                                                    <Send className="w-3.5 h-3.5" /> Disburse
                                                </button>
                                            )}
                                            {canDisburse && (loan.status === "approved" || loan.status === "finance_approved" || loan.status === "disbursed") && (
                                                <button
                                                    onClick={() => downloadBankAdvice(loan)}
                                                    className="p-2 text-ink/70 hover:text-klein hover:bg-klein/5"
                                                    title="Download bank advice CSV"
                                                    data-testid={`loan-bank-advice-${loan.id}`}
                                                >
                                                    <FileDown className="w-4 h-4" />
                                                </button>
                                            )}
                                            {canManage && (loan.status === "pending" || loan.status === "rejected") && (
                                                <button onClick={() => onDelete(loan)} className="p-2 text-ink/60 hover:text-destructive hover:bg-destructive/5" title="Delete" data-testid={`loan-delete-${loan.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="loan-create-dialog">
                    <form onSubmit={onCreate} onClick={(e) => e.stopPropagation()} className="bg-white border border-ink/10 w-full max-w-lg">
                        <div className="flex items-center justify-between px-8 py-6 border-b border-ink/10">
                            <div>
                                <div className="overline">New request</div>
                                <h3 className="mt-1 font-display font-bold text-xl">Loan / salary advance</h3>
                            </div>
                            <button type="button" onClick={() => setOpen(false)} className="p-2 text-ink/60 hover:bg-ink/5">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-8 py-6 grid grid-cols-2 gap-5">
                            <div className="col-span-2">
                                <label className="overline block mb-2">Employee *</label>
                                {selectedEmp ? (
                                    <div className="flex items-center justify-between border border-klein bg-klein/5 px-3 py-2.5" data-testid="loan-employee-selected">
                                        <div className="text-sm">
                                            <div className="font-medium text-ink">{selectedEmp.first_name} {selectedEmp.last_name}</div>
                                            <div className="text-xs text-ink/60">
                                                {selectedEmp.employee_no || "—"}
                                                {selectedEmp.department ? ` · ${selectedEmp.department}` : ""}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { setForm({ ...form, employee_id: "" }); setEmpSearch(""); }}
                                            className="p-1 text-ink/50 hover:text-ink"
                                            data-testid="loan-employee-clear"
                                            aria-label="Change employee"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="relative">
                                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40 pointer-events-none" />
                                            <input
                                                type="search"
                                                placeholder="Search name, staff no, department, division, phone…"
                                                value={empSearch}
                                                onChange={(e) => setEmpSearch(e.target.value)}
                                                className="w-full border border-ink/15 py-2.5 pl-9 pr-3 text-sm focus:border-klein focus:outline-none"
                                                data-testid="loan-employee-search"
                                                autoFocus
                                            />
                                        </div>
                                        <div className="mt-2 max-h-56 overflow-y-auto border border-ink/10" data-testid="loan-employee-list">
                                            {filteredEmployees.length === 0 ? (
                                                <div className="px-3 py-4 text-xs text-ink/40">No matches.</div>
                                            ) : filteredEmployees.map((e) => (
                                                <button
                                                    key={e.id}
                                                    type="button"
                                                    onClick={() => { setForm({ ...form, employee_id: e.id }); setEmpSearch(""); }}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-bone border-b border-ink/5 last:border-b-0"
                                                    data-testid={`loan-employee-option-${e.id}`}
                                                >
                                                    <div className="font-medium text-ink">{e.first_name} {e.last_name}</div>
                                                    <div className="text-xs text-ink/50">
                                                        {e.employee_no || "—"}
                                                        {e.department ? ` · ${e.department}` : ""}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                        {!empSearch && employees.length > 50 && (
                                            <p className="text-[11px] text-ink/40 mt-1">
                                                Showing first 50 of {employees.length}. Type to search all.
                                            </p>
                                        )}
                                        {/* Hidden input keeps required-state meaningful */}
                                        <input type="hidden" required value={form.employee_id} readOnly />
                                    </>
                                )}
                            </div>
                            <div>
                                <label className="overline block mb-2">Type</label>
                                <div className="border border-ink/15 py-2.5 px-3 text-sm bg-bone/40 text-ink/70" data-testid="loan-type-display">
                                    {form.type === "advance" ? "Salary advance (single deduction)" : "Loan (multi-month)"}
                                </div>
                            </div>
                            <div>
                                <label className="overline block mb-2">{form.type === "advance" ? "Deduction period" : "Tenure (months)"}</label>
                                {form.type === "advance" ? (
                                    <div className="border border-ink/15 py-2.5 px-3 text-sm bg-bone/40 text-ink/70">
                                        Next payroll
                                    </div>
                                ) : (
                                    <input
                                        type="number"
                                        min="1"
                                        max="60"
                                        value={form.tenure_months}
                                        onChange={(e) => setForm({ ...form, tenure_months: e.target.value })}
                                        className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                        data-testid="loan-tenure-input"
                                    />
                                )}
                            </div>
                            <div className="col-span-2">
                                <label className="overline block mb-2">Amount (₦) *</label>
                                <MoneyInput
                                    required
                                    value={form.amount}
                                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="loan-amount-input"
                                />
                                <p className="text-xs text-ink/50 mt-2">
                                    {form.type === "advance"
                                        ? "Full amount is deducted once, from the first payroll run after it is disbursed."
                                        : <>Monthly installment will be approximately <span className="font-semibold text-ink">{fmt(monthlyPreview)}</span>.</>}
                                </p>
                            </div>
                            <div className="col-span-2">
                                <label className="overline block mb-2">Deduction starts (optional)</label>
                                <input
                                    type="month"
                                    value={form.start_period}
                                    onChange={(e) => setForm({ ...form, start_period: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="loan-start-period-input"
                                />
                                <p className="text-xs text-ink/50 mt-2">Leave blank to start with the next open payroll. Any month works unless its payroll is already finalised. The accountant confirms the month when disbursing.</p>
                            </div>
                            <div className="col-span-2">
                                <label className="overline block mb-2">Reason</label>
                                <textarea
                                    rows="3"
                                    value={form.reason}
                                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="loan-reason-input"
                                />
                            </div>
                        </div>
                        <div className="px-8 py-5 border-t border-ink/10 flex items-center justify-end gap-3">
                            <button type="button" onClick={() => setOpen(false)} className="px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5">Cancel</button>
                            <button type="submit" disabled={creating} className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="loan-submit-button">
                                {creating ? "Submitting…" : "Submit request"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
            {bulkOpen && (
                <BulkLoanDialog
                    canAutoApprove={canManage}
                    onClose={() => setBulkOpen(false)}
                    onDone={load}
                />
            )}
            {openingOpen && (
                <BulkOpeningBalancesDialog
                    onClose={() => setOpeningOpen(false)}
                    onDone={load}
                />
            )}
        </div>
    );
}

function BulkOpeningBalancesDialog({ onClose, onDone }) {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);

    const downloadTemplate = async () => {
        try {
            const res = await api.get("/employees/opening-balances/template", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
            const a = document.createElement("a");
            a.href = url; a.download = "opening_balances_template.xlsx";
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Template downloaded — active employees are pre-filled");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Download failed");
        }
    };

    const upload = async () => {
        if (!file) { toast.error("Choose an .xlsx file first"); return; }
        setUploading(true); setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/employees/opening-balances/bulk", fd);
            setResult(data);
            toast.success(`Imported: ${data.created_loans} loan(s), ${data.created_advances} advance(s)`);
            onDone();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="opening-balances-dialog">
            <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-5 border-b border-ink/10">
                    <div className="overline">Migration · one-time</div>
                    <h3 className="mt-1 font-display font-bold text-2xl tracking-tight text-ink">Opening loan &amp; advance balances</h3>
                    <p className="mt-2 text-xs text-ink/60">
                        Import carry-over balances from your previous system. Approved records will be created and picked up by the next payroll run.
                    </p>
                </div>
                <div className="p-6 space-y-5">
                    <div className="bg-bone p-4 border border-ink/10">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="text-xs font-semibold text-ink">Step 1 · Download template</div>
                                <p className="text-xs text-ink/60 mt-1">Pre-filled with all active employees.</p>
                            </div>
                            <button onClick={downloadTemplate} className="text-xs px-3 py-2 border border-ink/20 hover:bg-ink hover:text-white whitespace-nowrap" data-testid="opening-balances-download-btn">
                                Download .xlsx
                            </button>
                        </div>
                    </div>
                    <div className="bg-bone p-4 border border-ink/10 space-y-3">
                        <div>
                            <div className="text-xs font-semibold text-ink">Step 2 · Fill and upload</div>
                            <p className="text-xs text-ink/60 mt-1">
                                Columns: <code className="text-[10px] bg-white px-1">opening_loan_balance</code>,
                                <code className="text-[10px] bg-white px-1 mx-1">opening_loan_installments</code> (number of months),
                                <code className="text-[10px] bg-white px-1">opening_advance_balance</code> (single-shot).
                            </p>
                        </div>
                        <input
                            type="file"
                            accept=".xlsx"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="text-xs w-full"
                            data-testid="opening-balances-file-input"
                        />
                        <button
                            onClick={upload}
                            disabled={!file || uploading}
                            className="w-full bg-klein text-white px-4 py-2.5 text-sm hover:bg-klein/90 disabled:opacity-40"
                            data-testid="opening-balances-upload-btn"
                        >
                            {uploading ? "Uploading…" : "Upload template"}
                        </button>
                    </div>
                    {result && (
                        <div className="bg-emerald-50 border border-emerald-200 p-4" data-testid="opening-balances-result">
                            <div className="text-xs font-semibold text-emerald-800">Import complete</div>
                            <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
                                <Stat label="Loans created" value={result.created_loans} />
                                <Stat label="Advances created" value={result.created_advances} />
                                <Stat label="Total imported (₦)" value={new Intl.NumberFormat("en-NG", { minimumFractionDigits: 2 }).format(result.total_amount || 0)} />
                            </div>
                            {result.not_found_count > 0 && (
                                <div className="mt-3 text-xs text-red-700">
                                    {result.not_found_count} row(s) skipped — employee not found. First: {result.not_found?.[0]?.employee_no}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t border-ink/10 flex justify-end">
                    <button onClick={onClose} className="px-4 py-2 text-sm border border-ink/15 hover:bg-ink hover:text-white">Close</button>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-ink/50">{label}</div>
            <div className="mt-0.5 font-display font-black text-xl tabular-nums text-ink">{value}</div>
        </div>
    );
}

function BulkLoanDialog({ canAutoApprove, onClose, onDone }) {
    const [downloading, setDownloading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [autoApprove, setAutoApprove] = useState(false);
    const [result, setResult] = useState(null);
    const fileRef = useRef(null);

    const onDownload = async () => {
        setDownloading(true);
        try {
            const { data } = await api.get("/bulk/loan-template", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = "loan_template.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Template downloaded. Fill type + amount + tenure + reason, then upload.");
        } catch (e) {
            toast.error("Could not download template");
        } finally {
            setDownloading(false);
        }
    };

    const onPick = () => fileRef.current?.click();
    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".xlsx")) {
            toast.error("Please upload an .xlsx file");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const qs = autoApprove ? "?auto_approve=true" : "";
            const { data } = await api.post(`/bulk/loan/import${qs}`, fd);
            setResult(data);
            toast.success(`Created ${data.created_count} loans/advances · total ₦${Number(data.total_amount).toLocaleString()}${data.auto_approved ? " (auto-approved)" : ""}`);
            onDone();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="bulk-loan-dialog">
            <div className="bg-white max-w-xl w-full rounded-lg shadow-2xl">
                <div className="px-6 py-4 border-b border-ink/10 flex items-center justify-between">
                    <h2 className="font-display font-bold text-lg flex items-center gap-2">
                        <FileText className="w-4 h-4 text-klein" /> Bulk loans &amp; advances via Excel
                    </h2>
                    <button onClick={onClose} className="text-ink/40 hover:text-ink" data-testid="bulk-loan-close">✕</button>
                </div>
                <div className="px-6 py-5 space-y-5">
                    <ol className="text-sm text-ink/70 space-y-2 list-decimal list-inside">
                        <li>Download the template — it&apos;s pre-filled with active employees + their current salary as reference.</li>
                        <li>For each employee you want to file: fill <code className="bg-bone px-1 rounded">type</code> (loan|advance), <code className="bg-bone px-1 rounded">amount</code>, <code className="bg-bone px-1 rounded">tenure_months</code> (advances always 1) and optional <code className="bg-bone px-1 rounded">reason</code>.</li>
                        <li>Leave amount blank to skip a row.</li>
                        <li>Upload — each row creates one loan/advance record.</li>
                    </ol>

                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={onDownload}
                            disabled={downloading}
                            className="inline-flex items-center justify-center gap-2 border border-klein text-klein px-4 py-3 text-sm rounded hover:bg-klein hover:text-white disabled:opacity-50"
                            data-testid="bulk-loan-download-btn"
                        >
                            <Download className="w-4 h-4" />
                            {downloading ? "Downloading…" : "1. Download template"}
                        </button>
                        <button
                            onClick={onPick}
                            disabled={uploading}
                            className="inline-flex items-center justify-center gap-2 bg-klein text-white px-4 py-3 text-sm rounded hover:bg-klein/90 disabled:opacity-50"
                            data-testid="bulk-loan-upload-btn"
                        >
                            <Upload className="w-4 h-4" />
                            {uploading ? "Uploading…" : "2. Upload filled file"}
                        </button>
                        <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={onUpload} data-testid="bulk-loan-file-input" />
                    </div>

                    {canAutoApprove && (
                        <label className="flex items-center gap-2 text-sm text-ink/70 cursor-pointer" data-testid="bulk-loan-auto-approve-label">
                            <input
                                type="checkbox"
                                checked={autoApprove}
                                onChange={(e) => setAutoApprove(e.target.checked)}
                                className="w-4 h-4 accent-klein"
                                data-testid="bulk-loan-auto-approve"
                            />
                            Auto-approve on creation (admin / HR only — skips the &quot;pending&quot; stage)
                        </label>
                    )}

                    {result && (
                        <div className="border border-ink/10 rounded p-4 bg-bone/40 space-y-2 text-sm" data-testid="bulk-loan-result">
                            <div className="flex justify-between"><span className="text-ink/60">Created</span><strong className="text-emerald-700">{result.created_count}</strong></div>
                            <div className="flex justify-between"><span className="text-ink/60">Skipped</span><strong>{result.skipped_count}</strong></div>
                            <div className="flex justify-between"><span className="text-ink/60">Not found</span><strong>{result.not_found_count}</strong></div>
                            <div className="flex justify-between border-t border-ink/10 pt-2"><span className="text-ink/60">Total principal</span><strong className="text-klein">₦{Number(result.total_amount).toLocaleString()}</strong></div>
                            {result.auto_approved && (
                                <div className="text-[11px] text-emerald-700 mt-1">All created entries auto-approved.</div>
                            )}
                            {result.skipped_count > 0 && result.skipped[0] && (
                                <div className="text-[11px] text-ink/50 mt-2">e.g. {result.skipped[0].reason}</div>
                            )}
                        </div>
                    )}

                    <div className="text-right pt-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm text-ink/60 hover:text-ink" data-testid="bulk-loan-close-2">Close</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
